import { Box, Grid } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import AlertLabel from 'components/PetCard/UI/AlertLabel';
import { vaccinesAttention, vaccinesWarning } from 'components/PetCard/utils/PetCardUtils';
import Pet from '@spike/pet-model';
import { FunctionComponent, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { approveVaccineApprovalThunk, declineVaccineApprovalThunk } from '@spike/vaccine-approvals-action';
import { getPetThunk } from '@spike/pets-action';
import { useApiClientWrapper, useAuth, usePermissions, usePetPendingVaccineApprovals } from 'hooks';
import { RootState } from 'store';
import { reduceResolution, wbp } from 'Theme';
import VaccinesTable from './VaccinesTable';
import { canReviewVaccineApproval, mergeVaccinesWithPendingApprovals } from './pendingVaccineApprovals';

interface VaccinesProps {
    pet: Pet;
    className?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        alerts: {
            [theme.breakpoints.down(wbp)]: {
                paddingBottom: `${reduceResolution(20)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                paddingBottom: '20px'
            },
            [theme.breakpoints.down('md')]: {
                '& p': {
                    fontSize: '14px',
                    fontWeight: 400
                }
            }
        },
        alertInfo: {
            [theme.breakpoints.down('md')]: {
                'fontSize': '14px',
                '& p': {
                    fontSize: '14px',
                    fontWeight: 400
                }
            }
        }
    })
);

export const Vaccines: FunctionComponent<VaccinesProps> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const apiClientWrapperRef = useRef(apiClientWrapper);
    apiClientWrapperRef.current = apiClientWrapper;

    const auth = useAuth();
    const permissions = usePermissions();
    const reduxPet = useSelector<RootState, Pet | undefined>(state => state.pets.pet);
    const pet = reduxPet?.id === props.pet.id && reduxPet ? reduxPet : props.pet;

    const { pendingApprovals, removePending, refresh } = usePetPendingVaccineApprovals(pet.id, pet.name);
    const [reviewingId, setReviewingId] = useState<number | null>(null);

    const vaccines = pet.medicalHistory?.vaccines || [];

    const rows = useMemo(
        () => mergeVaccinesWithPendingApprovals(vaccines, pendingApprovals),
        [vaccines, pendingApprovals]
    );

    const hasVaccines = rows.length > 0;
    const hasWarning = vaccinesAttention(vaccines) || vaccinesWarning(vaccines) || pendingApprovals.length > 0;

    const canReviewApproval = useCallback(
        (approvalId: number) => {
            const approval = pendingApprovals.find(item => item.id === approvalId);
            if (!approval) return false;
            return canReviewVaccineApproval(approval, permissions.clients.manage, auth.marketplaceId);
        },
        [pendingApprovals, permissions.clients.manage, auth.marketplaceId]
    );

    const refreshVaccines = useCallback(async () => {
        if (!pet.id) return;
        await (dispatch as any)(getPetThunk(apiClientWrapperRef.current, pet.id));
        await refresh();
    }, [dispatch, pet.id, refresh]);

    const handleAccept = useCallback(
        async (approvalId: number) => {
            if (reviewingId !== null) return;
            setReviewingId(approvalId);
            try {
                await (dispatch as any)(approveVaccineApprovalThunk(apiClientWrapperRef.current, approvalId));
                removePending(approvalId);
                await refreshVaccines();
            } finally {
                setReviewingId(null);
            }
        },
        [dispatch, refreshVaccines, removePending, reviewingId]
    );

    const handleDecline = useCallback(
        async (approvalId: number) => {
            if (reviewingId !== null) return;
            setReviewingId(approvalId);
            try {
                await (dispatch as any)(declineVaccineApprovalThunk(apiClientWrapperRef.current, approvalId));
                // Quitar pending primero: la fila vuelve al PetVaccine anterior del historial
                removePending(approvalId);
                await refreshVaccines();
            } finally {
                setReviewingId(null);
            }
        },
        [dispatch, refreshVaccines, removePending, reviewingId]
    );

    return (
        <Box className={clsx(props.className)}>
            <Grid container>
                <Grid
                    item
                    xs={12}
                >
                    {(!hasVaccines || (hasVaccines && hasWarning)) && (
                        <Box className={classes.alerts}>
                            {!hasVaccines && (
                                <AlertLabel
                                    className={classes.alertInfo}
                                    label="No vaccines added yet"
                                    type="info"
                                />
                            )}
                            {hasVaccines && hasWarning && (
                                <AlertLabel
                                    className={classes.alertInfo}
                                    label={`${pet.name} has one or more vaccines pending review.`}
                                    type="attention"
                                />
                            )}
                        </Box>
                    )}
                    <Box>
                        {hasVaccines && (
                            <VaccinesTable
                                rows={rows}
                                canReviewApproval={canReviewApproval}
                                onAccept={handleAccept}
                                onDecline={handleDecline}
                            />
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Vaccines;
